import { Syringe, Info } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    vaccines: {
      name: string;
      dose: string;
      date: string;
      batchNo: string;
      manufacturer: string;
      administeredBy: string;
      site: string;
    }[];
    postInstructions?: string[];
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

export default function VaccinationCertificateTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Syringe className="w-4 h-4 text-gray-700" />
        <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
          Vaccination Record
        </span>
      </div>

      {/* Vaccine Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[10px] min-w-[500px]">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Vaccine Name
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Dose
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Date
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Batch No.
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Manufacturer
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Administered By
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Site
              </th>
            </tr>
          </thead>
          <tbody>
            {data.vaccines.map((v, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="py-1 px-1.5 border border-gray-200 font-medium text-gray-800">
                  {v.name}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">{v.dose}</td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">{v.date}</td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-600 font-mono">
                  {v.batchNo}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                  {v.manufacturer}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                  {v.administeredBy}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">{v.site}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Post-Vaccination Instructions */}
      {data.postInstructions && data.postInstructions.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Info className="w-3 h-3 text-gray-600" />
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
              Post-Vaccination Instructions
            </h3>
          </div>
          <ol className="list-decimal list-inside space-y-1 pl-1">
            {data.postInstructions.map((instruction, i) => (
              <li key={i} className="text-[10px] text-gray-700 leading-snug">
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
